// Only keep the typed version and import once
import React, { useState, useEffect } from 'react';
import { dateToTimestamp, timestampToDate } from './dateUtils';

export interface Member {
  id: number;
  name: string;
  email: string;
  nfc_token_id?: string;
  credits: number;
  securityRoles: string[];
  login_allowed?: boolean;
  sports?: { id: number; name: string }[];
  date_of_birth?: number;  // timestamp (ms)
  gender?: string;
  join_date?: number;      // timestamp (ms)
  membership_type?: string;
  membership_status?: string;
}

interface EditMemberFormProps {
  member: Member;
  onSave: (member: Member, sportsRoles: { sport_id: number; role_ids: number[] }[]) => void;
  onCancel: () => void;
  onSportsChange?: (sportIds: number[]) => void;
  userRoles?: string[];
  formTitle?: string;
  accessToken?: string | null;
  serverError?: string;
}

interface MemberFormState {
  name: string;
  email: string;
  nfc_token_id: string;
  credits: number;
  sport_ids: number[];
  date_of_birth: string;
  gender: string;
  join_date: string;
  membership_type: string;
  membership_status: string;
  securityRoles: string[];
  login_allowed: boolean;
}

export function getRoleOptions(sports: {id: number, name: string}[]) {
  const baseRoles = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
    { value: 'trustee', label: 'Trustee' },
  ];
  const sportAdminRoles = sports.map(sport => ({
    value: `sports_admin_${sport.name.toLowerCase().replace(/\s+/g, '_')}`,
    label: `Sports Admin (${sport.name})`
  }));
  return [...baseRoles, ...sportAdminRoles];
}

function formatDate(value: unknown): string {
  if (!value) return '';
  const s = String(value);
  // Handle timestamp numbers (may be negative for pre-1970 dates): convert back to YYYY-MM-DD using UTC
  if (/^-?\d+$/.test(s)) {
    const date = new Date(Number(s));
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return s.includes('T') ? s.slice(0, 10) : s;
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

const EditMemberForm: React.FC<EditMemberFormProps> = (props) => {
  const { member, onSave, onCancel, onSportsChange, userRoles, formTitle, accessToken, serverError: propServerError } = props;
  const [form, setForm] = useState<MemberFormState>({
    name: member.name || '',
    email: member.email || '',
    nfc_token_id: member.nfc_token_id || '',
    credits: member.credits || 0,
    sport_ids: member.sports ? member.sports.map(s => s.id) : [],
    date_of_birth: formatDate(member.date_of_birth),
    gender: member.gender || '',
    join_date: formatDate(member.join_date),
    membership_type: member.membership_type || '',
    membership_status: member.membership_status || '',
    securityRoles: Array.isArray(member.securityRoles) ? member.securityRoles.map(r => r.toLowerCase()) : [],
    login_allowed: !!member.login_allowed,
  });
  const [sports, setSports] = useState<{id: number, name: string}[]>([]);
  const [error, setError] = useState('');
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [joinDateError, setJoinDateError] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [sportRoles, setSportRoles] = useState<{[sportId: number]: {id: number, role_name: string}[]}>( {});
  const [selectedRoles, setSelectedRoles] = useState<{[sportId: number]: number[]}>({});

  useEffect(() => {
    setForm({
      name: member.name || '',
      email: member.email || '',
      nfc_token_id: member.nfc_token_id || '',
      credits: member.credits || 0,
      sport_ids: member.sports ? member.sports.map(s => s.id) : [],
      date_of_birth: formatDate(member.date_of_birth),
      gender: member.gender || '',
      join_date: formatDate(member.join_date),
      membership_type: member.membership_type || '',
      membership_status: member.membership_status || '',
      securityRoles: Array.isArray(member.securityRoles) ? member.securityRoles.map(r => r.toLowerCase()) : [],
      login_allowed: !!member.login_allowed,
    });
  }, [member]);
  useEffect(() => {
    fetch('/api/sports', {
      headers: { 
        'Content-Type': 'application/json',
        ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
      }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setSports(data);
      });
  }, [accessToken]);

  // Fetch roles for selected sports
  useEffect(() => {
    async function fetchRoles() {
      const rolesMap: {[sportId: number]: {id: number, role_name: string}[]} = {};
      for (const sportId of form.sport_ids) {
        const res = await fetch(`/api/sports/${sportId}/roles`, {
          headers: {
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
          }
        });
        const data = await res.json();
        if (Array.isArray(data)) rolesMap[sportId] = data;
      }
      setSportRoles(rolesMap);
    }
    if (form.sport_ids.length > 0) fetchRoles();
  }, [form.sport_ids, accessToken]);

  // Fetch member's roles for each sport on mount
  useEffect(() => {
    async function fetchMemberRoles() {
      if (!member.id || member.id === 0) return;
      const res = await fetch(`/api/members/${member.id}/sport_roles`, {
        headers: {
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
        }
      });
      const data = await res.json();
      const rolesBySport: {[sportId: number]: number[]} = {};
      if (Array.isArray(data)) {
        for (const sport of data) {
          rolesBySport[sport.sport_id] = sport.roles.map((r: any) => r.role_id);
        }
      }
      setSelectedRoles(rolesBySport);
    }
    fetchMemberRoles();
  }, [member.id, accessToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = e.target;
    if (target instanceof HTMLSelectElement && target.multiple && target.name === 'sport_ids') {
      const selected = Array.from(target.selectedOptions).map(o => Number(o.value));
      console.log('Sport select changed:', selected);
      setForm(prev => ({ ...prev, sport_ids: selected }));
      if (onSportsChange) onSportsChange(selected);
    } else if (target instanceof HTMLInputElement && target.type === 'checkbox' && target.name === 'sport_ids') {
      setForm(prev => {
        const current = prev.sport_ids;
        const checked = target.checked;
        const sportId = Number(target.value);
        const updated = checked ? [...current, sportId] : current.filter(id => id !== sportId);
        console.log('Sport checkbox changed:', updated);
        if (onSportsChange) onSportsChange(updated);
        return { ...prev, sport_ids: updated };
      });
    } else if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setForm((prev) => ({ ...prev, [target.name]: target.checked }));
    } else {
      setForm((prev) => ({ ...prev, [target.name]: target.value }));
    }
  };

  // Handle role selection change
  const handleRoleChange = (sportId: number, roleIds: number[]) => {
    setSelectedRoles(prev => ({ ...prev, [sportId]: roleIds }));
  };

  // Fix: convert selectedRoles to string[] for select value
  const getRoleSelectValue = (sportId: number) =>
    (selectedRoles[sportId] ? selectedRoles[sportId].map(String) : []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setNameError(!form.name.trim());
    setEmailError(!form.email.trim());
    setDateError(!form.date_of_birth);
    setJoinDateError(!form.join_date);
    setGenderError(!form.gender);
    if (!form.name.trim() || !form.email.trim() || !form.date_of_birth || !form.join_date || !form.gender) {
      return;
    }
    if (!form.date_of_birth.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setDateError(true);
      return;
    }
    if (!form.join_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      setJoinDateError(true);
      return;
    }
    // Prepare roles payload
    const sportsRoles = Object.entries(selectedRoles).map(([sportId, roleIds]) => ({ sport_id: Number(sportId), role_ids: roleIds }));
    // Convert date strings to timestamps for backend transport
    const payload = { ...member, ...form } as unknown as Member;
    payload.date_of_birth = dateToTimestamp(form.date_of_birth);
    payload.join_date = dateToTimestamp(form.join_date);
    // Save member, pass sportsRoles as second argument
    onSave(payload, sportsRoles);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-xl w-full max-w-5xl relative mx-auto">
      <h3 className="text-2xl font-bold mb-8 text-left">{formTitle || 'Edit Member'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        {/* Name */}
        <div className="flex flex-col">
          <label className="text-gray-700 text-sm font-bold mb-1">Name</label>
          <input
            type="text"
            name="name"
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${nameError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
            value={form.name || ''}
            onChange={e => { handleChange(e); setNameError(false); }}
            required
          />
          {nameError && <span className="text-red-500 text-xs mt-1">Name is required</span>}
        </div>
        {/* Email */}
        <div className="flex flex-col">
          <label className="text-gray-700 text-sm font-bold mb-1">Email</label>
          <input
            type="email"
            name="email"
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${emailError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
            value={form.email || ''}
            onChange={e => { handleChange(e); setEmailError(false); }}
            required
          />
          {emailError && <span className="text-red-500 text-xs mt-1">Email is required</span>}
        </div>
        {/* Date of Birth */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Date of Birth</label>
          <input
            type="date"
            name="date_of_birth"
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${dateError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
            value={form.date_of_birth}
            onChange={e => { handleChange(e); setDateError(false); }}
            required
          />
        </div>
        {/* Gender */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Gender *</label>
          <select
            name="gender"
            className={`flex-1 shadow border rounded py-2 px-3 text-gray-700 focus:outline-none ${genderError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
            value={form.gender}
            onChange={e => { handleChange(e); setGenderError(false); }}
            required
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        {/* Join Date */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Join Date</label>
          <input
            type="date"
            name="join_date"
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${joinDateError ? 'border-red-500 focus:border-red-500' : 'focus:shadow-outline'}`}
            value={form.join_date}
            onChange={e => { handleChange(e); setJoinDateError(false); }}
            required
          />
        </div>
        {/* Membership Type */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Membership Type</label>
          <select
            name="membership_type"
            className="flex-1 shadow border rounded py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
            value={form.membership_type}
            onChange={handleChange}
          >
            <option value="">Select</option>
            <option value="Associate">Associate</option>
            <option value="Senior">Senior</option>
            <option value="Junior">Junior</option>
            <option value="Life">Life</option>
          </select>
        </div>
        {/* Membership Status */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Membership Status</label>
          <input
            type="text"
            name="membership_status"
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${hasRole(userRoles, 'admin') ? 'focus:shadow-outline' : 'bg-gray-100'}`}
            value={form.membership_status}
            onChange={handleChange}
            disabled={!hasRole(userRoles, 'admin')}
          />
        </div>
        {/* Sports */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Sports</label>
          <div className="flex flex-wrap gap-4">
            {sports.map(sport => (
              <div key={sport.id} className="flex flex-col items-start mr-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="sport_ids"
                    value={sport.id}
                    checked={form.sport_ids.includes(sport.id)}
                    onChange={e => {
                      const checked = e.target.checked;
                      setForm(prev => {
                        const current = prev.sport_ids;
                        return {
                          ...prev,
                          sport_ids: checked
                            ? [...current, sport.id]
                            : current.filter(id => id !== sport.id)
                        };
                      });
                    }}
                  />
                  {sport.name}
                </label>
                {/* Roles dropdown for selected sport */}
                {form.sport_ids.includes(sport.id) && sportRoles[sport.id] && sportRoles[sport.id].length > 0 && (
                  <select
                    multiple
                    className="mt-2 border rounded px-2 py-1 text-sm"
                    value={getRoleSelectValue(sport.id)}
                    onChange={e => {
                      const options = Array.from(e.target.selectedOptions).map(o => Number(o.value));
                      handleRoleChange(sport.id, options);
                    }}
                  >
                    {sportRoles[sport.id].map(role => (
                      <option key={role.id} value={role.id}>{role.role_name}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
        {/* NFC Token ID */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">NFC Token ID</label>
          <input
            type="text"
            name="nfc_token_id"
            className={`flex-1 shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none ${hasRole(userRoles, 'admin') ? 'focus:shadow-outline' : 'bg-gray-100'}`}
            value={form.nfc_token_id || ''}
            onChange={handleChange}
            disabled={!hasRole(userRoles, 'admin')}
          />
        </div>
        {/* Credits */}
        <div className="flex items-center">
          <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Credits</label>
          <input
            type="number"
            name="credits"
            value={form.credits}
            onChange={handleChange}
            className="flex-1 border rounded px-3 py-2"
            min="0"
          />
        </div>
        {/* Role selection (admin or trustee only) - checkbox list */}
        {(hasRole(userRoles, 'admin') || hasRole(userRoles, 'trustee')) && (
          <div className="flex flex-col">
            <label className="text-gray-700 text-sm font-bold mb-1">Roles</label>
            <div className="flex flex-col gap-2 border rounded py-2 px-3">
              {getRoleOptions(sports).map(opt => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.securityRoles.includes(opt.value)}
                    onChange={e => {
                      setForm(prev => ({
                        ...prev,
                        securityRoles: e.target.checked
                          ? [...prev.securityRoles, opt.value]
                          : prev.securityRoles.filter(r => r !== opt.value)
                      }));
                    }}
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {/* Allow Login checkbox (admin only) */}
        {hasRole(userRoles, 'admin') && (
          <div className="flex items-center">
            <label className="w-40 text-gray-700 text-sm font-bold mr-4 text-right">Allow Login</label>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="login_allowed"
                checked={form.login_allowed}
                onChange={handleChange}
              />
              <span className="text-sm text-gray-600">{form.login_allowed ? 'Yes' : 'No'}</span>
            </div>
          </div>
        )}
      </div>
      {(error || propServerError) && <div className="mb-4 text-red-600 font-semibold">{propServerError || error}</div>}
      <div className="flex justify-end gap-2 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md"
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default EditMemberForm;
